import React, { useEffect, useState } from 'react';
import { useDebounce } from './hooks/useDebounce';
import { ActionCardContainer } from './ActionCardContainer';
import { isValidContentfulId, isValidNbaActionCode } from './utils/isValidId';
import styled from 'styled-components';
import {
  TextField,
  ErrorNotification,
  Stack,
  Toggle,
  Heading1,
} from '@ovotech/element-v5';
import { Page } from '@/src/components/Pages/Pages';
import { useLocation, useHistory } from 'react-router-dom-v5';

export const PageContainer = styled.div(
  ({ theme: { space } }) => `
  width: 100%;
  max-width: 800px;
  margin: ${space[4]} auto;
  padding: 0 ${space[4]};
`,
);

export const ActionCardsPreviewScreen = () => {
  const location = useLocation();
  const history = useHistory();

  const [actionCode, setActionCode] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('action-code') || '';
  });

  const [containerId, setContainerId] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('container-id') || '';
  });

  const [previewMode, setPreviewMode] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('preview') === 'true';
  });

  const debouncedActionCode = useDebounce(actionCode, 500);
  const debouncedContainerId = useDebounce(containerId, 500);

  const isActionCodeValid =
    debouncedActionCode === '' || isValidNbaActionCode(debouncedActionCode);
  const isContainerIdValid = isValidContentfulId(debouncedContainerId);

  const isReadyToFetch = isContainerIdValid && isActionCodeValid;

  useEffect(() => {
    const params = new URLSearchParams();

    if (debouncedActionCode) {
      params.set('action-code', debouncedActionCode);
    }

    if (debouncedContainerId) {
      params.set('container-id', debouncedContainerId);
    }

    if (previewMode) {
      params.set('preview', 'true');
    }

    const newSearch = params.toString() ? `?${params.toString()}` : '';

    // only update if the search string changed
    if (newSearch !== location.search) {
      history.replace({ search: newSearch });
    }
  }, [
    debouncedActionCode,
    debouncedContainerId,
    previewMode,
    history,
    location.search,
  ]);

  return (
    <Page title="Action cards preview">
      <PageContainer>
        <Stack spaceBetween={2}>
          <Heading1>Action cards preview</Heading1>
          <Toggle
            label="Enable preview mode"
            hint="This will show unpublished Contentful changes"
            id="preview-toggle"
            checked={previewMode}
            onChange={() => setPreviewMode(prev => !prev)}
          />
          <Stack spaceBetween={2}>
            <TextField
              id="actionCode"
              name="actionCode"
              label="NBA action code"
              placeholder="NBA action code"
              value={actionCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setActionCode(e.target.value);
              }}
              error={!isActionCodeValid}
            />
            {!isActionCodeValid && (
              <ErrorNotification
                id="invalidActionCode"
                title="Invalid action code"
              />
            )}
          </Stack>
          <Stack spaceBetween={2}>
            <TextField
              id="contentfulContainerId"
              name="contentfulContainerId"
              label="Contentful container id"
              placeholder="Contentful container id"
              value={containerId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setContainerId(e.target.value);
              }}
              error={!isContainerIdValid}
            />
            {debouncedContainerId && !isContainerIdValid && (
              <ErrorNotification
                id="invalidContainerId"
                title="Invalid container id"
              />
            )}
          </Stack>

          {isReadyToFetch && (
            <ActionCardContainer
              actionCode={debouncedActionCode}
              containerId={debouncedContainerId}
              previewMode={previewMode}
            />
          )}
        </Stack>
      </PageContainer>
    </Page>
  );
};
